import React from "react";
import { Badge } from "react-bootstrap";

import Constants from "../../constants";
import { EnrollmentAdvisorDTO, EnrollmentNoteDTO, ENROLLMENT_NOTE_TYPE } from "../../models/EnrollmentHistoryDTO";
import { formatLocalDateTime } from "./format";

export interface EnrollmentNotesTimelineProps {
	notes: EnrollmentNoteDTO[];
	createdAt?: string | null;
	createdBy?: string | null;
	advisors?: EnrollmentAdvisorDTO[];
	/** Legacy single-text note, shown only when there is no history yet. */
	legacyNotes?: string | null;
	/** Current enrollment status; when not active, the last update (who/when) is shown. */
	statusId?: number | null;
	status?: string | null;
	updatedAt?: string | null;
	updatedBy?: string | null;
	compact?: boolean;
}

const noteBadge = (typeId: number): string => {
	switch (typeId) {
		case ENROLLMENT_NOTE_TYPE.ALTA: return "success";
		case ENROLLMENT_NOTE_TYPE.ACTUALIZACION: return "warning";
		default: return "secondary";
	}
};

export const describeAdvisors = (advisors: EnrollmentAdvisorDTO[] | undefined): string => {
	if (!advisors || advisors.length < 1) {
		return "-";
	}
	return advisors.map((advisor) => advisor.name).join(", ");
};

/** Who enrolled the student, who sold it, and every note with its author and date. Alta and actualizaciones look different. */
const ACTIVE_STATUS = 1;

const EnrollmentNotesTimeline: React.FC<EnrollmentNotesTimelineProps> = ({ notes, createdAt, createdBy, advisors, legacyNotes, statusId, status, updatedAt, updatedBy, compact }) => {
	const hasHistory = notes && notes.length > 0;
	const showUpdate = statusId !== undefined && statusId !== null && statusId !== ACTIVE_STATUS && (updatedBy || updatedAt);
	return (
		<div className="enrollmentTimeline" style={{ fontSize: compact ? 13 : 14 }}>
			{(createdAt || createdBy) ? (
				<div style={{ color: "var(--wa-apagado)", marginBottom: 4 }}>
					{Constants.Common.enrolledBy} <b style={{ color: "inherit" }}>{createdBy || "—"}</b>
					{createdAt ? <> · {formatLocalDateTime(createdAt)}</> : ""}
				</div>
			) : ""}
			{advisors !== undefined ? (
				<div style={{ marginBottom: (hasHistory || showUpdate) ? 6 : 0 }}>
					<span style={{ color: "var(--wa-apagado)" }}>{Constants.Common.advisors}:</span> {describeAdvisors(advisors)}
				</div>
			) : ""}
			{showUpdate ? (
				<div style={{ color: "var(--wa-apagado)", marginBottom: hasHistory ? 8 : 0 }}>
					<b style={{ color: "inherit" }}>{updatedBy || "—"}</b> {Constants.Common.updatedEnrollmentTo} <b style={{ color: "inherit" }}>{status || statusId}</b>
					{updatedAt ? <> · {formatLocalDateTime(updatedAt)}</> : ""}
				</div>
			) : ""}
			{hasHistory ? (
				<ul style={{ listStyle: "none", padding: 0, margin: 0, borderLeft: "2px solid var(--wa-acento-suave)" }}>
					{notes.map((note) => (
						<li key={"note_" + note.id} style={{ padding: "4px 0 6px 12px", position: "relative" }}>
							<span style={{
								position: "absolute", left: -6, top: 10, width: 10, height: 10, borderRadius: "50%",
								background: note.typeId === ENROLLMENT_NOTE_TYPE.ALTA ? "#198754" : note.typeId === ENROLLMENT_NOTE_TYPE.ACTUALIZACION ? "#FFC107" : "var(--wa-tenue)"
							}} />
							<div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
								<Badge bg={noteBadge(note.typeId)} text={note.typeId === ENROLLMENT_NOTE_TYPE.ACTUALIZACION ? "dark" : undefined}>{note.type}</Badge>
								<span style={{ color: "var(--wa-apagado)", fontSize: 12 }}>{note.createdBy || "—"} · {formatLocalDateTime(note.createdAt)}</span>
							</div>
							<div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 2 }}>{note.note}</div>
						</li>
					))}
				</ul>
			) : (legacyNotes ? (
				<div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginTop: 4 }}>
					<Badge bg="light" text="dark">{Constants.Common.notes}</Badge> {legacyNotes}
				</div>
			) : "")}
		</div>
	);
};

export default EnrollmentNotesTimeline;
